import { cn } from '@ui/lib/utils';
import Link from 'next/link';
import type { ComponentProps } from 'react';

export interface LocalizedLinkProps extends ComponentProps<typeof Link> {
  locale: string;
  href: string;
  variant?: 'link' | 'primary' | 'secondary' | 'accent' | 'ghost' | 'reverse' | 'alternative';
}

const variantStyles = {
  link: cn(
    'text-gray-600 hover:text-gray-800 hover:underline',
    'focus:outline-none focus:ring-2 focus:ring-gray-600 focus:ring-offset-2',
    'transition-colors duration-200'
  ),
  alternative: cn(
    'text-secondary-600 hover:text-secondary-800 hover:underline',
    'focus:outline-none focus:ring-2 focus:ring-secondary-600 focus:ring-offset-2',
    'transition-colors duration-200'
  ),
  primary: cn(
    'inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg',
    'bg-primary-600 text-white border border-transparent cursor-pointer',
    'hover:bg-primary-800 focus:outline-none focus:ring-2',
    'focus:ring-primary-800 focus:ring-offset-2',
    'transition-colors duration-200'
  ),
  secondary: cn(
    'inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg',
    'bg-secondary-600 text-white border border-transparent cursor-pointer',
    'hover:bg-secondary-800 focus:outline-none focus:ring-2',
    'focus:ring-secondary-800 focus:ring-offset-2',
    'transition-colors duration-200'
  ),
  accent: cn(
    'inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg',
    'bg-transparent text-secondary-800 border border-transparent cursor-pointer',
    'hover:bg-secondary-100 hover:text-secondary-800 focus:outline-none focus:ring-2',
    'focus:ring-secondary-800 focus:ring-offset-2',
    'transition-colors duration-200'
  ),

  reverse: cn(
    'inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg',
    'bg-primary-400 text-white border border-primary-600 cursor-pointer ',
    'hover:bg-primary-800 focus:outline-none focus:ring-2',
    'focus:ring-primary-800 focus:ring-offset-2',
    'transition-colors duration-200'
  ),
  ghost: 'hover:bg-gray-100 text-gray-700 hover:text-gray-900 focus-visible:ring-gray-500 cursor-pointer',

};

const SUPPORTED_LOCALES = ['en-us', 'fr-fr', 'es-419', 'de-de', 'pt-br', 'ja-jp'];

function buildLocalizedHref(locale: string, href: string): string {
  if (!href.startsWith('/')) {
    return href;
  }

  const cleanHref = href.slice(1);
  const segments = cleanHref.split('/');

  if (SUPPORTED_LOCALES.includes(segments[0])) {
    return `/${cleanHref}`;
  }

  return `/${locale}/${cleanHref}`;
}

export function LocalizedLink({
  locale,
  href,
  className,
  variant = 'link',
  ...props
}: LocalizedLinkProps) {
  const localizedHref = buildLocalizedHref(locale, href);

  return (
    <Link
      {...props}
      href={localizedHref}
      prefetch={false}
      className={cn(variantStyles[variant], className)}
    />
  );
}
